import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Capacitor } from '@capacitor/core';
import { createHashRouter, RouterProvider, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, isFirebaseConfigured } from './firebase';
import { ensureUserProfile } from './services/invites';
import { inspectionFirebaseConfigured } from './services/inspectionFirebase';
import InspectionProjectApp from './InspectionProjectApp';
import { CssBaseline, ThemeProvider } from '@mui/material';
import fleetTheme from './theme/fleetTheme';

// Lazy load components for better performance
const Login = lazy(() => import('./auth/Login'));
const CompanySignup = lazy(() => import('./auth/CompanySignup'));
const AcceptInvite = lazy(() => import('./auth/AcceptInvite'));
const AcceptFleetInvite = lazy(() => import('./auth/AcceptFleetInvite'));

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="loading-container">
      <div className="loading-spinner">
        <div className="loading-logo">
          <span className="loading-text-dark">LogistiX</span>
          <span className="loading-text-blue">nerd</span>
        </div>
        <div className="loading-bar">
          <div className="loading-bar-fill"></div>
        </div>
        <p>Loading...</p>
      </div>
    </div>
  );
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const isNativePlatform = Capacitor.isNativePlatform();
  const fleetRootPath = isNativePlatform
    ? '/fleet-inspections/mobile'
    : '/fleet-inspections/web';

  // Check authentication state
  useEffect(() => {
    if (!isFirebaseConfigured) {
      // Demo mode - check for demo user
      const demoUser = localStorage.getItem('demo_user');
      if (demoUser) {
        setUser(JSON.parse(demoUser));
      }
      setAuthLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLoginSuccess = (loggedInUser) => {
    setUser(loggedInUser);
  };

  const handleSignupSuccess = async (newUser) => {
    console.log('Signup successful:', newUser);
    setUser(newUser);

    if (newUser?.uid) {
      try {
        await ensureUserProfile({
          uid: newUser.uid,
          email: newUser.email,
          displayName: newUser.displayName,
          role: 'manager',
          companyId: newUser.uid
        });
      } catch (error) {
        console.error('Failed to persist manager profile:', error);
      }
    }
  };

  return (
    <Routes>
      <Route path="/fleet-invite" element={isNativePlatform
        ? <Navigate to={fleetRootPath} replace />
        : <AcceptFleetInvite />} />
      <Route path="/fleet-inspections/web/*" element={isNativePlatform
        ? <Navigate to="/fleet-inspections/mobile" replace />
        : <InspectionProjectApp surface="web" />} />
      <Route path="/fleet-inspections/mobile/*" element={isNativePlatform
        ? <InspectionProjectApp surface="mobile" />
        : <Navigate to="/fleet-inspections/web" replace />} />
      <Route path="/fleet-inspections" element={<Navigate to={fleetRootPath} replace />} />
      {/* ===== PUBLIC ROUTES ===== */}
      
      {/* Landing page - device detection & app selection */}
      <Route 
        path="/" 
        element={
          inspectionFirebaseConfigured ? <Navigate to={fleetRootPath} replace /> :
          authLoading ? <LoadingSpinner /> : user ? (
            <Navigate to={fleetRootPath} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        } 
      />
      
      {/* Login */}
      <Route 
        path="/login" 
        element={
          isNativePlatform ? <Navigate to={fleetRootPath} replace /> : authLoading ? <LoadingSpinner /> : user ? <Navigate to={fleetRootPath} replace /> : (
            <Login
              onSwitchToSignup={() => window.location.href = '/signup'}
              onLoginSuccess={handleLoginSuccess}
            />
          )
        } 
      />
      
      {/* Signup */}
      <Route 
        path="/signup" 
        element={
          isNativePlatform ? <Navigate to={fleetRootPath} replace /> : authLoading ? <LoadingSpinner /> : user ? <Navigate to={fleetRootPath} replace /> : (
            <CompanySignup
              onSwitchToLogin={() => window.location.href = '/login'}
              onSignupSuccess={handleSignupSuccess}
            />
          )
        } 
      />

      <Route
        path="/accept-invite"
        element={isNativePlatform ? <Navigate to={fleetRootPath} replace /> : <AcceptInvite onInviteAccepted={handleLoginSuccess} />}
      />

      {/* Manager dashboard is not part of the driver app */}
      <Route path="/manager/*" element={<Navigate to={fleetRootPath} replace />} />

      {/* Inspection records require the separately provisioned inspection-project account. */}
      <Route path="/inspector/*" element={<Navigate to={fleetRootPath} replace />} />

      {/* Fallback - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const [router] = useState(() => createHashRouter([{
    path: '*',
    element: <Suspense fallback={<LoadingSpinner />}><AppContent /></Suspense>,
  }]));
  return (
    <ThemeProvider theme={fleetTheme}>
      <CssBaseline />
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}